// @flow

import { now } from 'lodash';
import type { ReceiverUiTransition } from 'Common/cast/types';
import { CastReceiver } from '.';

type QueueItem = {|
  transition: ReceiverUiTransition,
  releaseInMs: number,
|};

type TransitionConfig = {| transition: ReceiverUiTransition, releaseInMs?: number |};

class TransitionsQueue {
  static POP_FROM_QUEUE_IN_MS = 1000;

  items: Array<QueueItem> = [];
  popFromQueueTimeOutId: TimeoutID;
  onItemRelease: ReceiverUiTransition => void;
  lastReleasedItemTsMs: number = 0;

  constructor(onItemRelease: ReceiverUiTransition => void) {
    this.onItemRelease = onItemRelease;
    this._scheduleNextTick();
  }

  add(item: TransitionConfig) {
    const { transition } = item;
    const releaseInMs = item.releaseInMs ?? TransitionsQueue.POP_FROM_QUEUE_IN_MS;

    if (releaseInMs === 0) {
      this._resetQueue();
    }

    this.items.push({ transition, releaseInMs });
    CastReceiver.log(`[CAST][STATE MACHINE] Received transition ${transition} (${now()})`);
  }

  _onTick = () => {
    if (this._isEmpty()) {
      this._scheduleNextTick();
      return;
    }

    const nextQueueItem: QueueItem = this._getNextTransition();
    const [shouldBeReleased, nextTickInMs] = this._isItTimeToReleaseElementFromQueue(nextQueueItem);

    if (shouldBeReleased) this._releaseItem(nextQueueItem);

    this._scheduleNextTick(nextTickInMs);
  };

  _releaseItem = (queueItem: QueueItem) => {
    this._remove(queueItem);
    this.onItemRelease(queueItem.transition);
    this.lastReleasedItemTsMs = now();
    CastReceiver.log(
      `[CAST][STATE MACHINE] Released transition ${queueItem.transition} (${now()})`,
    );
  };

  _scheduleNextTick = (inMs: number = TransitionsQueue.POP_FROM_QUEUE_IN_MS) => {
    clearTimeout(this.popFromQueueTimeOutId);
    this.popFromQueueTimeOutId = setTimeout(this._onTick, inMs);
  };

  _getNextTransition = () => this.items[0];

  _isItTimeToReleaseElementFromQueue = (transition: QueueItem): [boolean, number] => {
    const { releaseInMs = TransitionsQueue.POP_FROM_QUEUE_IN_MS } = transition;

    const timeSinceLastItemRelease = now() - this.lastReleasedItemTsMs;

    return timeSinceLastItemRelease >= releaseInMs
      ? [true, TransitionsQueue.POP_FROM_QUEUE_IN_MS]
      : [false, releaseInMs - timeSinceLastItemRelease];
  };

  _resetQueue = () => {
    CastReceiver.log('[CAST][STATE MACHINE] Cleared transitions queue.');
    if (this._isEmpty() === false) {
      /* Release all elements one by one to avoid cases with invalid transitions */
      [...this.items].forEach(this._releaseItem);
    }

    this._scheduleNextTick(0);
  };

  _remove = (itemToRemove: QueueItem) => {
    this.items = this.items.filter(item => item !== itemToRemove);
  };

  _isEmpty = () => this.items.length === 0;
}

export { TransitionsQueue };
